import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TextInput } from 'react-native';
import Animated, {
    Extrapolation,
    SharedValue,
    interpolate,
    useAnimatedStyle,
    useSharedValue,
} from 'react-native-reanimated';

import ClearIcon from '@/assets/icons/clear';
import SearchIcon from '@/assets/icons/pickem-search';
import { Text } from '@/components/TextComponent';
import { Row, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import {
    CANCEL_BUTTON_WIDTH,
    SEARCH_BAR_HEIGHT,
    SEARCH_BAR_PADDING,
    SEARCH_ICON_WIDTH,
    SEARCH_TEXT_WIDTH,
    useSearchDimensions,
} from '@/feature/search/hooks/use-search-dimensions';
import { designSystem } from '@/styles/styles';

type SearchBarProps = {
    query?: string;
    setQuery?: (query: string) => void;
    inputRef?: React.RefObject<TextInput | null>;
    onFocus?: () => void;
    onSubmitEditing?: () => void;
    onPressCancel?: () => void;
    animatedValue: SharedValue<number>;
};

const { neutral300 } = designSystem.colors;

export const SearchBar = memo(
    ({ query, setQuery, inputRef, onFocus, onSubmitEditing, animatedValue, onPressCancel }: SearchBarProps) => {
        const { t } = useTranslation(['common', 'search']);
        const { fullSearchBarWidth, searchMaxWidthOnSearchScreen } = useSearchDimensions();

        const animatedCancelStyle = useAnimatedStyle(() => {
            return {
                // make the "cancel" button appear only after the search bar has expanded at least 80%
                // so it has the "space" to appear
                opacity: interpolate(animatedValue.value ?? 0, [0, 1], [0, 1], Extrapolation.CLAMP),
                transform: [
                    {
                        translateX: interpolate(animatedValue.value ?? 0, [0, 1], [50, 0], Extrapolation.CLAMP),
                    },
                ],
            };
        });

        const animatedWidthStyle = useAnimatedStyle(() => {
            return {
                width: interpolate(
                    animatedValue.value,
                    [0, 1],
                    [fullSearchBarWidth, searchMaxWidthOnSearchScreen],
                    Extrapolation.CLAMP
                ),
            };
        });
        const fullInputWidth = fullSearchBarWidth - CANCEL_BUTTON_WIDTH - SEARCH_ICON_WIDTH;

        const hasText = useSharedValue((query?.length ?? 0) > 0);
        useEffect(() => {
            hasText.value = (query?.length ?? 0) > 0;
        }, [query, hasText]);

        // Width of {icon + gap + 'Search'} used to compute the centering offset
        const placeholderContentWidth = SEARCH_ICON_WIDTH + 4 + SEARCH_TEXT_WIDTH;

        const animatedRowStyle = useAnimatedStyle(() => {
            if (hasText.value) {
                // Text present: anchor icon to the left, no centering
                return { paddingLeft: SEARCH_BAR_PADDING };
            }
            // Placeholder: animate paddingLeft so {icon + 'Search'} is centered in the collapsed bar
            const innerWidth = fullSearchBarWidth - SEARCH_BAR_PADDING * 2;
            const centeringOffset = Math.max(0, (innerWidth - placeholderContentWidth) / 2);
            return {
                paddingLeft: interpolate(
                    animatedValue.value,
                    [1, 0],
                    [SEARCH_BAR_PADDING, SEARCH_BAR_PADDING + centeringOffset],
                    Extrapolation.CLAMP
                ),
            };
        });

        const animatedInputWidthStyle = useAnimatedStyle(() => {
            if (hasText.value) {
                return { width: fullInputWidth };
            }
            return {
                width: interpolate(
                    animatedValue.value,
                    [1, 0],
                    [fullInputWidth, SEARCH_TEXT_WIDTH],
                    Extrapolation.CLAMP
                ),
            };
        });

        const animatedClearIconStyle = useAnimatedStyle(() => ({
            opacity: animatedValue.value ?? 0,
        }));

        const handleOnPress = useCallback(() => {
            inputRef?.current?.focus();
        }, [inputRef]);

        return (
            <Row alignItems={'center'}>
                <Animated.View style={[styles.searchBarContainer, animatedWidthStyle]}>
                    <TouchableBox activeOpacity={1} onPress={handleOnPress} flex={1}>
                        <Animated.View style={[styles.rowWrapper, animatedRowStyle]}>
                            <SearchIcon color={neutral300} />
                            <Animated.View style={[styles.inputWrapper, animatedInputWidthStyle]}>
                                <TextInput
                                    pointerEvents={'auto'}
                                    style={[styles.input, styles.text]}
                                    placeholder={t('search:placeholder')}
                                    placeholderTextColor={neutral300}
                                    keyboardAppearance={'dark'}
                                    value={query}
                                    onChangeText={text => setQuery?.(text)}
                                    returnKeyType={'done'}
                                    onSubmitEditing={onSubmitEditing}
                                    ref={inputRef}
                                    onFocus={onFocus}
                                />
                            </Animated.View>
                            {query && query.length > 0 ? (
                                <Animated.View style={[styles.clearIconContainer, animatedClearIconStyle]}>
                                    <TouchableBox
                                        onPress={() => {
                                            inputRef?.current?.focus();
                                            setQuery?.('');
                                        }}
                                        paddingRight={'s16'}
                                        paddingLeft={'s12'}
                                        paddingVertical={'s10'}
                                    >
                                        <ClearIcon />
                                    </TouchableBox>
                                </Animated.View>
                            ) : null}
                        </Animated.View>
                    </TouchableBox>
                </Animated.View>
                <Animated.View style={[styles.cancelButtonContainer, animatedCancelStyle]}>
                    <TouchableBox
                        paddingLeft={'s16'}
                        onPress={onPressCancel}
                        paddingVertical={'s8'}
                        paddingRight={'s16'}
                    >
                        <Text variant="bodyMedium">{t('common:cancel')}</Text>
                    </TouchableBox>
                </Animated.View>
            </Row>
        );
    }
);

export const styles = StyleSheet.create({
    input: {
        textAlignVertical: 'center',
        padding: 0,
    },
    inputWrapper: {
        overflow: 'hidden',
        marginLeft: 4,
    },
    text: {
        color: neutral300,
        fontSize: 17,
        fontWeight: '400',
        letterSpacing: -0.45,
    },
    rowWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    clearIconContainer: {
        position: 'absolute',
        right: 0,
    },
    cancelButtonContainer: {
        position: 'absolute',
        right: 0,
        width: CANCEL_BUTTON_WIDTH,
    },
    searchBarContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        height: SEARCH_BAR_HEIGHT,
        borderWidth: 1,
        borderRadius: 24,
        borderCurve: 'continuous',
        overflow: 'hidden',
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderColor: ds.colorValue.border.subtle,
    },
});
